import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { AnchorTextLink, Tooltip, Text, Stack, Box, Shelf, Input } from '@chaser/components';
import { useOnMount } from '@chaser/components';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import styles from './Totp.module.scss';
import phoneImg from './phone.svg';
import downloadImg from './download.svg';
import { Form, Field, useFormik, FormikProvider } from 'formik';
import usePush from '../../../../hooks/usePush';
import LoadingMessage from '../../../../components/LoadingMessage';

import AuthenticationService from '../../../../services/authentication-service';
import useOnUpdate from '../../../../hooks/useOnUpdate';
import useMedia from '../../../../hooks/useMedia';

const canCopyToClipboard = !!navigator.clipboard?.writeText;

// eslint-disable-next-line react/prop-types
const TotpSetupForm = ({ bodyAs = 'div', buttons, onSubmit, isLoading, isComplete, tempToken }) => {
  const isMobile = useMedia('(max-width: 600px)');

  const { execute: executeSetup, loading: loadingSetup, response: setupResponse } = usePush(
    AuthenticationService.setupTotp,
  );

  const [copied, setCopied] = useState(false);

  async function copyKey() {
    try {
      await navigator.clipboard.writeText(setupResponse.key);
      setCopied(true);
    } catch (e) {
      //
    }
  }

  useOnMount(() => {
    executeSetup(tempToken);
  });

  useEffect(() => {
    isLoading(loadingSetup);
  }, [isLoading, loadingSetup]);

  const form = useFormik({
    initialValues: {
      _reinit: loadingSetup,
      code: '',
    },
    enableReinitialize: true,
    onSubmit,
  });

  useOnUpdate(() => {
    if (!form.values.code) return;
    isComplete(form.values.code);
  }, [form.values, form.isValid]);

  const BarcodeComp = isMobile ? Stack : Shelf;
  const FormBody = bodyAs;

  return (
    <FormikProvider value={form}>
      <Form>
        <Stack gap="medium">
          <FormBody>
            <Stack gap="medium">
              <Stack gap="medium" dividers>
                <Shelf gap="large" px="small" flexWrap="nowrap">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    className={styles.icon}
                  >
                    <img src={downloadImg} alt="" />
                  </Box>
                  <Stack>
                    <Text fontWeight="bold">1. Get the app</Text>
                    <Text>
                      Download the free Google Authenticator (
                      <AnchorTextLink href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US">
                        Android
                      </AnchorTextLink>{' '}
                      /{' '}
                      <AnchorTextLink href="https://apps.apple.com/us/app/google-authenticator/id388497605">
                        iOS
                      </AnchorTextLink>
                      ) on your mobile. We don't recommend paying for an authenticator app.
                    </Text>
                  </Stack>
                </Shelf>
                <BarcodeComp className={styles.qrShelf} gap="large" px="small" flexWrap="nowrap">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    className={cx(styles.icon, styles.qr)}
                  >
                    {setupResponse ? <QRCode value={setupResponse.uri} /> : <LoadingMessage />}
                  </Box>
                  <Stack>
                    <Text fontWeight="bold">2. Scan this barcode</Text>
                    <Text>Open the authentication app:</Text>
                    <Text>
                      <ul>
                        <li>Tap “+” and choose "Scan a QR code"</li>
                        <li>Scan the image to the right, using your phone's camera</li>
                        <li>
                          <Text trim={false} color="Mid Neutral 1">
                            Alternatively, enter this key: <br />
                            {canCopyToClipboard ? (
                              <Tooltip
                                content={copied ? 'Copied!' : 'Click to copy'}
                                position="top"
                                afterHide={() => setCopied(false)}
                              >
                                <button
                                  type="button"
                                  className={styles.copyButton}
                                  onClick={copyKey}
                                >
                                  <code className={styles.key}>{setupResponse?.key}</code>
                                </button>
                              </Tooltip>
                            ) : (
                              <code className={styles.key}>{setupResponse?.key}</code>
                            )}
                          </Text>
                        </li>
                      </ul>
                    </Text>
                  </Stack>
                </BarcodeComp>
                <Shelf gap="large" px="small" flexWrap="nowrap">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    className={styles.icon}
                  >
                    <img src={phoneImg} alt="" />
                  </Box>
                  <Stack>
                    <Text fontWeight="bold">3. Enter verification code</Text>
                    <Text>Enter the 6-digit code generated by the app.</Text>
                    <Field
                      name="code"
                      as={Input}
                      placeholder="Code"
                      inputMode="numeric"
                      autoFocus
                    />
                  </Stack>
                </Shelf>
              </Stack>
            </Stack>
          </FormBody>
          {buttons}
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default TotpSetupForm;
